import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiSearch, FiEdit3, FiDownload } from "react-icons/fi";
import { formAPI } from "../../../../../services/api";
import "./10DQT.css";

const VIP10DQT = ({ projectId = "vip10" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subjects, setSubjects] = useState([]); // 전체 데이터 저장
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [filteredSubjects, setFilteredSubjects] = useState([]); // 검색 필터링된 데이터
  const [paginatedSubjects, setPaginatedSubjects] = useState([]); // 페이지네이션된 데이터

  const ITEMS_PER_PAGE = 20;
  const SEARCH_FIELDS = [
    "pt_name",
    "pt_number",
    "response_time",
    "scene_time",
    "transport_time",
  ];

  // URL 쿼리 파라미터 관리
  const currentParams = {
    search: searchParams.get("search") || "",
    page: parseInt(searchParams.get("page")) || 1,
  };

  // 쿼리 파라미터 업데이트 함수
  const updateQueryParams = (updates) => {
    const newParams = { ...currentParams, ...updates };
    setSearchParams(
      Object.entries(newParams)
        .filter(([_, value]) => value !== null && value !== "")
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    );
  };

  // 검색어 입력 핸들러
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // 검색 버튼 클릭 핸들러
  const handleSearch = () => {
    updateQueryParams({ search: searchInput, page: 1 });
  };

  // 검색 입력에서 Enter 키 처리
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    updateQueryParams({ page: newPage });
  };

  // 시간 관련 에러 데이터 다운로드 핸들러
  const handleDownloadTimeErrors = async () => {
    setDownloading(true);
    try {
      console.log("시간 관련 에러 데이터 다운로드 시작...");

      // VIP10 조건부 데이터 다운로드 API 호출
      const downloadUrl =
        "https://product.idea2m.com/api/vip10/export-filtered?format=csv";

      console.log("다운로드 URL:", downloadUrl);

      // 새 창에서 다운로드 실행
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.download = `VIP10_TimeErrors_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("시간 관련 에러 데이터 다운로드 완료");
    } catch (error) {
      console.error("시간 관련 에러 데이터 다운로드 실패:", error);
      alert(`다운로드 실패: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  // 시간 에러 조건에 맞는 데이터만 조회
  useEffect(() => {
    const fetchFilteredData = async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorDetails(null);

        console.log("[10DQT] 시간 에러 조건 데이터 조회 시작:", { projectId });

        // VIP10 전용 백엔드 API를 사용하여 전체 데이터 조회 후 클라이언트에서 필터링
        const vip10ApiUrl = "https://product.idea2m.com/api/vip10/get-data";

        const response = await fetch(vip10ApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            search: "", // 검색어 없이 전체 데이터 조회
            searchFields: SEARCH_FIELDS,
          }),
        });

        if (!response.ok) {
          throw new Error(
            `API 호출 실패: ${response.status} ${response.statusText}`
          );
        }

        const responseData = await response.json();

        if (responseData.success) {
          // 시간 관련 에러 조건에 맞는 데이터만 필터링
          const filteredData = responseData.rows.filter((row) => {
            const formData = row.form_data || {};
            const responseTime = parseInt(formData.response_time) || 0;
            const sceneTime = parseInt(formData.scene_time) || 0;
            const transportTime = parseInt(formData.transport_time) || 0;
            const arrestLocation = formData.arrest_location || "";

            // 시간 관련 에러 조건들
            const responseTimeError =
              (responseTime <= 0 &&
                !["병원 이송 중", "타 병원"].includes(arrestLocation)) ||
              responseTime >= 90;

            const sceneTimeError =
              (sceneTime <= 0 &&
                !["병원 이송 중", "타 병원"].includes(arrestLocation)) ||
              sceneTime >= 90;

            const transportTimeError =
              (transportTime <= 0 &&
                !["병원 이송 중", "타 병원"].includes(arrestLocation)) ||
              transportTime >= 90;

            // 하나라도 에러 조건에 해당하면 포함
            return responseTimeError || sceneTimeError || transportTimeError;
          });

          setSubjects(filteredData);
          setTotalCount(filteredData.length);

          console.log("[10DQT] 시간 에러 데이터 필터링 완료:", {
            totalRows: responseData.rows.length,
            filteredRows: filteredData.length,
            errorRate: `${(
              (filteredData.length / responseData.rows.length) *
              100
            ).toFixed(1)}%`,
          });
        } else {
          throw new Error(responseData.message || "데이터 조회 실패");
        }
      } catch (err) {
        console.error("[10DQT] 데이터 조회 중 오류:", err);
        setErrorDetails({
          step: err.response?.data?.step,
          message: err.response?.data?.message,
          error: err.response?.data?.error,
          sql: err.response?.data?.error?.sql,
          sqlMessage: err.response?.data?.error?.sqlMessage,
        });
        setError(err.message || "데이터를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredData();
  }, [projectId]);

  // 클라이언트 사이드 검색 필터링
  useEffect(() => {
    let filtered = subjects;

    // 검색어가 있는 경우 필터링
    if (currentParams.search && currentParams.search.trim()) {
      const searchTerm = currentParams.search.toLowerCase().trim();
      filtered = subjects.filter((subject) => {
        return SEARCH_FIELDS.some((field) => {
          const value = subject.form_data?.[field] || subject[field];
          return value && value.toString().toLowerCase().includes(searchTerm);
        });
      });
    }

    setFilteredSubjects(filtered);

    console.log("[10DQT] 클라이언트 필터링 완료:", {
      totalSubjects: subjects.length,
      filteredSubjects: filtered.length,
      searchTerm: currentParams.search,
    });
  }, [subjects, currentParams.search]);

  // 클라이언트 사이드 페이지네이션
  useEffect(() => {
    const startIndex = (currentParams.page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginated = filteredSubjects.slice(startIndex, endIndex);

    setPaginatedSubjects(paginated);

    console.log("[10DQT] 페이지네이션 적용:", {
      currentPage: currentParams.page,
      startIndex,
      endIndex,
      paginatedCount: paginated.length,
      totalPages: Math.ceil(filteredSubjects.length / ITEMS_PER_PAGE),
    });
  }, [filteredSubjects, currentParams.page]);

  // 페이지네이션 컴포넌트
  const Pagination = () => {
    const filteredTotalCount = filteredSubjects.length;
    const totalPages = Math.ceil(filteredTotalCount / ITEMS_PER_PAGE);
    const currentPage = currentParams.page;

    if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          전체 {filteredTotalCount}건 / {totalPages} 페이지 중 {currentPage}{" "}
          페이지
        </div>
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            이전
          </button>
          <span className="current-page-indicator">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="pagination-button"
          >
            다음
          </button>
        </div>
      </div>
    );
  };

  const handleEditSubject = (subject) => {
    console.log("[10DQT] 편집 요청:", {
      dataId: subject.data_id,
    });

    navigate(`/ecrf/subjectlist/edit/${subject.data_id}`, {
      state: {
        projectId: "10",
        formData: subject.form_data,
      },
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Complete: { color: "#10b981", bg: "#d1fae5" },
      "In process": { color: "#f59e0b", bg: "#fef3c7" },
      "Not started": { color: "#ef4444", bg: "#fee2e2" },
    };

    const config = statusConfig[status] || statusConfig["Not started"];

    return (
      <span
        className="status-badge"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {status || "Not started"}
      </span>
    );
  };

  const renderCellContent = (subject, column) => {
    const value = subject.form_data?.[column.id] || subject[column.id];

    switch (column.type) {
      case "status":
        return getStatusBadge(value);
      case "date":
        return value ? new Date(value).toLocaleDateString() : "-";
      case "time":
        // 시간 필드의 경우 에러 상태 표시
        if (column.id.includes("time")) {
          const timeValue = parseInt(value);
          if (timeValue <= 0 || timeValue >= 90) {
            return (
              <span style={{ color: "#ef4444", fontWeight: "bold" }}>
                {value || "-"} ⚠️
              </span>
            );
          }
        }
        return value || "-";
      default:
        return value || "-";
    }
  };

  // 컬럼 정의
  const columns = [
    {
      id: "__edit__",
      label: "Edit",
      type: "edit",
      sortable: false,
      width: "80px",
    },
    {
      id: "pt_name",
      label: "pt_name",
      type: "text",
      sortable: true,
      width: "150px",
    },
    {
      id: "pt_number",
      label: "pt_number",
      type: "text",
      sortable: true,
      width: "150px",
    },
    {
      id: "response_time",
      label: "Response Time",
      type: "time",
      sortable: true,
      width: "140px",
    },
    {
      id: "scene_time",
      label: "Scene Time",
      type: "time",
      sortable: true,
      width: "140px",
    },
    {
      id: "transport_time",
      label: "Transport Time",
      type: "time",
      sortable: true,
      width: "140px",
    },
  ];

  if (loading) {
    return (
      <div className="vip-dqt-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>VIP10 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vip-dqt-container">
        <div className="error-message">
          <h3>오류가 발생했습니다</h3>
          <p>{error}</p>
          {errorDetails && (
            <div className="error-details">
              <h4>상세 오류 정보:</h4>
              <ul>
                {errorDetails.step && <li>발생 단계: {errorDetails.step}</li>}
                {errorDetails.message && (
                  <li>오류 메시지: {errorDetails.message}</li>
                )}
                {errorDetails.error &&
                  typeof errorDetails.error === "object" && (
                    <li>
                      추가 정보:
                      <pre>{JSON.stringify(errorDetails.error, null, 2)}</pre>
                    </li>
                  )}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="vip-dqt-container">
      {/* 데이터 다운로드 섹션 */}
      <div className="download-section">
        <div className="download-card">
          <h3>
            <FiDownload style={{ marginRight: "8px" }} />
            시간 관련 에러 데이터 다운로드
          </h3>
          <div className="download-description">
            <p>
              <strong>다운로드 조건:</strong>
            </p>
            <ul>
              <li>
                <strong>Response Time:</strong> 0분 이하 또는 90분 이상
                <span className="condition-note">
                  (단, "병원 이송 중", "타 병원"은 0분 허용)
                </span>
              </li>
              <li>
                <strong>Scene Time:</strong> 0분 이하 또는 90분 이상
                <span className="condition-note">
                  (단, "병원 이송 중", "타 병원"은 0분 허용)
                </span>
              </li>
              <li>
                <strong>Transport Time:</strong> 0분 이하 또는 90분 이상
                <span className="condition-note">
                  (단, "병원 이송 중", "타 병원"은 0분 허용)
                </span>
              </li>
            </ul>
            <p className="download-note">
              위 조건 중 하나라도 해당하는 케이스들을 CSV 파일로 다운로드합니다.
            </p>
          </div>
          <button
            className="download-button time-errors"
            onClick={handleDownloadTimeErrors}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <div className="spinner-small"></div>
                다운로드 중...
              </>
            ) : (
              <>
                <FiDownload />
                시간 에러 데이터 다운로드
              </>
            )}
          </button>
        </div>
      </div>

      {/* 검색 섹션 */}
      <div className="filter-section">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder={`${SEARCH_FIELDS.join(", ")} 검색...`}
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleSearchKeyPress}
          />
          <button onClick={handleSearch} className="search-button" title="검색">
            <FiSearch className="search-button-icon" />
          </button>
        </div>
      </div>

      {/* 테이블 섹션 */}
      <div className="table-container">
        <table className="subject-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={column.sortable ? "sortable" : ""}
                  style={{ width: column.width }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedSubjects.map((subject) => (
              <tr key={subject.id || subject.data_id}>
                {columns.map((column) => (
                  <td key={column.id}>
                    {column.id === "__edit__" ? (
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditSubject(subject)}
                        title="Edit"
                      >
                        <FiEdit3 />
                      </button>
                    ) : (
                      renderCellContent(subject, column)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination />

      {paginatedSubjects.length === 0 && !loading && (
        <div className="empty-state">
          <p>
            {filteredSubjects.length === 0
              ? "조건에 맞는 데이터가 없습니다."
              : "현재 페이지에 표시할 데이터가 없습니다."}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "16px",
              padding: "8px 16px",
              backgroundColor: "#1976D2",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
            </svg>
            새로고침
          </button>
        </div>
      )}
    </div>
  );
};

export default VIP10DQT;
